import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNavigation from '../../components/navigation/AdminNavigation';
import BreadcrumbNavigation from '../../components/navigation/BreadcrumbNavigation';
import Button from '../../components/ui/Button';
import { useAuth } from '../../components/navigation/AuthenticationGuard';
import { usersService } from '../../services/usersService';
import UsersTable from './components/UsersTable';
import FilterControls from './components/FilterControls';
import InviteUserModal from './components/InviteUserModal';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import EmptyState from './components/EmptyState';

const AdminUsersManagement = () => {
  const navigate = useNavigate();
  const { userProfile, isAdmin } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    role: 'all',
    search: ''
  });
  const [sortConfig, setSortConfig] = useState({
    column: 'createdAt',
    direction: 'desc'
  });
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/admin-dashboard');
      return;
    }
    loadUsers();
  }, [isAdmin, navigate]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await usersService?.getAllUsers();
      setUsers(data);
    } catch (err) {
      setError(err?.message || 'Failed to load users');
      console.error('Users load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users?.filter(user => {
      const matchesSearch = user?.email?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
                           user?.fullName?.toLowerCase()?.includes(filters?.search?.toLowerCase());
      const matchesRole = filters?.role === 'all' || user?.role === filters?.role;
      return matchesSearch && matchesRole;
    });

    filtered?.sort((a, b) => {
      let aValue = a?.[sortConfig?.column];
      let bValue = b?.[sortConfig?.column];

      if (sortConfig?.column === 'createdAt' || sortConfig?.column === 'lastLogin') {
        aValue = new Date(aValue)?.getTime() || 0;
        bValue = new Date(bValue)?.getTime() || 0;
      } else if (typeof aValue === 'string') {
        aValue = aValue?.toLowerCase();
        bValue = bValue?.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig?.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig?.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [users, filters, sortConfig]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      role: 'all',
      search: ''
    });
  };

  const handleSort = (column) => {
    setSortConfig((prev) => ({
      column,
      direction: prev?.column === column && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleInviteUser = async (email, role, fullName) => {
    try {
      const result = await usersService?.inviteUser(email, role, fullName);
      setSuccessMessage(result?.message);
      setInviteModalOpen(false);
      setTimeout(() => setSuccessMessage(''), 5000);
      await loadUsers();
    } catch (err) {
      throw err;
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await usersService?.updateUserRole(userId, newRole);
      setSuccessMessage('Role updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      await loadUsers();
    } catch (err) {
      setError(err?.message || 'Failed to update role');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      await usersService?.deleteUser(userToDelete?.id);
      setSuccessMessage('User deleted successfully');
      setDeleteModalOpen(false);
      setUserToDelete(null);
      setTimeout(() => setSuccessMessage(''), 3000);
      await loadUsers();
    } catch (err) {
      setError(err?.message || 'Failed to delete user');
      setDeleteModalOpen(false);
      setTimeout(() => setError(''), 5000);
    }
  };

  const openDeleteModal = (user) => {
    setUserToDelete(user);
    setDeleteModalOpen(true);
  };

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/admin-dashboard' },
    { label: 'Users Management', path: '/admin-users-management' }
  ];

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <BreadcrumbNavigation items={breadcrumbItems} />
        
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Users Management</h1>
            <p className="text-muted-foreground mt-2">Manage user accounts and roles</p>
          </div>
          <Button
            variant="default"
            iconName="UserPlus"
            iconPosition="left"
            onClick={() => setInviteModalOpen(true)}
          >
            Invite User
          </Button>
        </div>

        {successMessage && (
          <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg">
            <p className="text-success text-sm">{successMessage}</p>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <p className="text-destructive text-sm">{error}</p>
          </div>
        )}

        <FilterControls
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
        />

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredAndSortedUsers?.length === 0 ? (
          <EmptyState
            onInviteUser={() => setInviteModalOpen(true)}
            hasFilters={filters?.role !== 'all' || filters?.search !== ''}
            onClearFilters={handleClearFilters}
          />
        ) : (
          <UsersTable
            users={filteredAndSortedUsers}
            currentUserId={userProfile?.id}
            onRoleChange={handleRoleChange}
            onDeleteUser={openDeleteModal}
            sortConfig={sortConfig}
            onSort={handleSort}
          />
        )}
      </div>

      {inviteModalOpen && (
        <InviteUserModal
          onClose={() => setInviteModalOpen(false)}
          onInvite={handleInviteUser}
        />
      )}

      {deleteModalOpen && (
        <DeleteConfirmationModal
          user={userToDelete}
          onClose={() => {
            setDeleteModalOpen(false);
            setUserToDelete(null);
          }}
          onConfirm={handleDeleteUser}
        />
      )}
    </div>
  );
};

export default AdminUsersManagement;